/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
    ICreditReview,
    IInvestigatorReviewDom,
    IInvestigatorReviewInt,
    IManagerReview,
    IssueType,
    NatureOfComplaintDomestic,
    NatureOfComplaintInternational,
    ReviewNextStep,
    ReviewType,
    RootCause
} from '@cod-monorepo/interfaces';
import {
    CreditReviewForm,
    FinanceReviewForm,
    InvestigationReviewDomesticForm,
    InvestigationReviewInternationalForm,
    ManagerReviewForm
} from '../models/cod-review.model';
import { CodMappingOperations } from './cod-mapping.operations';

export class CodReviewMappingOperations {
    public static mapCreditReviewFormFromIcreditReview(data: ICreditReview, attachments: string[]): CreditReviewForm {
        return {
            financialAdjustment: data.financialAdjustment?.toString(),
            creditReviewRelatedDocuments: CodMappingOperations.getAttachments(
                attachments,
                'creditReviewRelatedDocuments'
            )
        };
    }

    public static mapICreditReviewFromCreditReviewForm(
        data: CreditReviewForm
    ): Omit<ICreditReview, 'id' | 'createdAt' | 'createdBy'> {
        return {
            financialAdjustment: Number(data.financialAdjustment)
        };
    }

    public static mapManagerReviewFormFromIManagerReview(reviews: IManagerReview[]): ManagerReviewForm {
        const lastReview = reviews.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())[0];

        return {
            relatedManagerReview: lastReview.reviewResult,
            reasonForDenial: lastReview.reason || '',
            nextStep: ReviewNextStep.CLOSE_COD
        };
    }

    public static mapIMangerReviewFromManagerReviewForm(
        data: ManagerReviewForm
    ): Omit<IManagerReview, 'id' | 'createdAt' | 'createdBy'> {
        return {
            type: ReviewType.MANAGER,
            reviewResult: data.relatedManagerReview,
            reason: data.reasonForDenial,
            nextStep: data.nextStep
        };
    }

    public static mapFinanceReviewFormFromIManagerReview(reviews: IManagerReview[]): FinanceReviewForm {
        const lastReview = reviews.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())[0];

        return {
            financeCodReview: lastReview.reviewResult,
            reasonForDenial: lastReview.reason || '',
            nextStep: ReviewNextStep.CLOSE_COD
        };
    }

    public static mapIManagerReviewFromFinanceReviewForm(
        data: FinanceReviewForm
    ): Omit<IManagerReview, 'id' | 'createdAt' | 'createdBy'> {
        return {
            type: ReviewType.FINANCE_MANAGER,
            reviewResult: data.financeCodReview,
            reason: data.reasonForDenial,
            nextStep: data.nextStep
        };
    }

    public static mapInvestigationReviewInternationalFormFromIInvestigatorReviewInt(
        data: IInvestigatorReviewInt,
        attachments: string[]
    ): InvestigationReviewInternationalForm {
        return {
            natureOfComplaint: data.natureOfComplaint ?? 'NO_CHANGE',
            issueType: data.issueType || null,
            rootCause: data.rootCause || null,
            through: data.through,
            details: data.details as string,
            investigationReviewRelatedDocuments: CodMappingOperations.getAttachments(
                attachments,
                'investigationReviewRelatedDocuments'
            ),
            investigatorReviewResult: data.reviewResult,
            reasonForDenial: data.reason as string
        };
    }

    public static mapIInvestigatorReviewIntFromInvestigationReviewInternationalForm(
        data: InvestigationReviewInternationalForm
    ): Omit<IInvestigatorReviewInt, 'id' | 'createdAt' | 'createdBy'> {
        return {
            natureOfComplaint: (data.natureOfComplaint !== 'NO_CHANGE'
                ? data.natureOfComplaint
                : undefined) as NatureOfComplaintInternational,
            issueType: data.issueType as IssueType,
            rootCause: data.rootCause as RootCause,
            through: data.through,
            details: data.details,
            reason: data.reasonForDenial,
            reviewResult: data.investigatorReviewResult
        };
    }

    public static mapInvestigationReviewDomesticFormFromIInvestigatorReviewDom(
        data: IInvestigatorReviewDom,
        attachments: string[]
    ): InvestigationReviewDomesticForm {
        return {
            natureOfComplaint: data.natureOfComplaint ?? 'NO_CHANGE',
            issueType: data.issueType || null,
            rootCause: data.rootCause || null,
            through: data.through,
            details: data.details as string,
            investigationReviewRelatedDocuments: CodMappingOperations.getAttachments(
                attachments,
                'investigationReviewRelatedDocuments'
            ),
            investigatorReviewResult: data.reviewResult,
            reasonForDenial: data.reason as string,
            csrResponsible: data.csrResponsible as string,
            nextStep: ReviewNextStep.CLOSE_COD
        };
    }

    public static mapIInvestigatorReviewDomFromInvestigationReviewDomesticForm(
        data: InvestigationReviewDomesticForm
    ): Omit<IInvestigatorReviewDom, 'id' | 'createdAt' | 'createdBy'> {
        return {
            natureOfComplaint: (data.natureOfComplaint !== 'NO_CHANGE'
                ? data.natureOfComplaint
                : undefined) as NatureOfComplaintDomestic,
            issueType: data.issueType as IssueType,
            rootCause: data.rootCause as RootCause,
            through: data.through,
            details: data.details,
            reason: data.reasonForDenial,
            reviewResult: data.investigatorReviewResult,
            csrResponsible: data.csrResponsible,
            nextStep: data.nextStep
        };
    }
}
